import { Component, OnInit, EventEmitter, Output, Input } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormArray } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, takeUntil, tap } from 'rxjs/operators';
import { fundsOriginLocation, fundsOriginOption } from './funds-origin';
import { RucExactLengthValidator } from 'src/app/shared/validators/ruc-exact-length.validator';
import { RequiredValidator } from 'src/app/shared/validators/required.validator';
import { LettersOnlyValidator } from 'src/app/shared/validators/letters-only.validators';
import { IDLengthBeneficiaryValidator } from 'src/app/shared/validators/id-length-beneficiary.validator';
import { IDFormatBeneficiaryValidator } from 'src/app/shared/validators/id-format-beneficiary.validator';
import { IDLengthLegalRepresentativeValidator } from 'src/app/shared/validators/id-length-legal-representative.validator';
import { IDFormatLegalRepresentativeValidator } from 'src/app/shared/validators/id-format-legal-representative.validator';
import { LaftService } from 'src/app/shared/services/laft.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { Roles } from 'src/app/shared/enums/Roles.enum';

@Component({
  selector: 'app-laft-company-beneficiary-form',
  templateUrl: './laft-company-beneficiary-form.component.html',
  styleUrls: ['./laft-company-beneficiary-form.component.scss'],
})
export class LaftCompanyBeneficiaryFormComponent implements OnInit {
  @Output() nextStep = new EventEmitter();
  @Output() formChangesEvent = new EventEmitter();
  @Input() userData: any;
  @Input() initData: any;

  readonly Roles = Roles;

  get role(): Roles {
    return this.userData?.role === Roles.CLIENT ? Roles.CLIENT : Roles.INVESTOR;
  }

  formSubmitSubject$ = new Subject();
  private destroy$ = new Subject<void>();
  public beneficiaryDataForm: FormGroup;
  public fundsOriginOptions = fundsOriginOption;
  public fundsOriginLocationOptions = fundsOriginLocation;
  public idTypeOptions = [
    { value: 'national id', text: 'DNI' },
    { value: 'foreigner card', text: 'Carnet de Extranjería' },
    { value: 'passport', text: 'Pasaporte' },
  ];
  public legalRepresentativeIdTypeOptions = [
    { value: 'national id', text: 'DNI' },
    { value: 'foreigner card', text: 'Carnet de Extranjería' },
    { value: 'passport', text: 'Pasaporte' },
    { value: 'ruc', text: 'RUC' },
    { value: 'other', text: 'Otro' },
  ];
  public taxReportErrorMessage: string = '';
  public isTaxReportValid: boolean = false;

  constructor(private formBuilder: FormBuilder, private laftService: LaftService, private spinner: NgxSpinnerService) {}

  ngOnInit(): void {
    this.beneficiaryDataForm = this.formBuilder.group({
      isBeneficiary: [true, [Validators.required]],
      beneficiaryRepresentativeType: [null, []],
      beneficiaryBusinessName: ['', []],
      beneficiaryRuc: ['', []],
      beneficiaryPowerType: ['acta', []],
      beneficiaryDocument: [null, []],
      beneficiaryPersonName: ['', []],
      beneficiaryPersonDocumentNumber: ['', []],
      beneficiaryPersonDocumentType: ['', []],
      beneficiaryPersonPowerType: ['', []],
      beneficiaryPersonDocument: [null, []],
      fundsOrigin: ['', []],
      fundsOriginOtherOption: ['', []],
      fundsOriginLocation: ['', []],
      fundsOriginLocationOtherOption: ['', []],
      isBeneficiaryPersonPep: [false, []],
      beneficiaryPersonCharge: ['', []],
      beneficiaryPersonInstitution: ['', []],
      taxReport: ['', [Validators.required]],
      shareHolders: this.formBuilder.array([]),
    });

    this.beneficiaryDataForm.valueChanges.pipe(debounceTime(300), distinctUntilChanged()).subscribe(() => {
      const rawValue = this.beneficiaryDataForm.getRawValue();
      let cleanedData = this.removeBeneficiaryRepresentativeFields(rawValue);
      cleanedData = this.removeEmptyFields(cleanedData);
      this.formChangesEvent.emit(cleanedData);
    });

    this.beneficiaryDataForm
      .get('taxReport')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        if (!value || value === '' || value === null) {
          this.taxReportErrorMessage = '';
          this.isTaxReportValid = false;
          // Clear legal representatives array
          this.shareHolders.clear();
          this.revertToDefaultValues();
          this.beneficiaryDataForm.get('isBeneficiary').disable();
          this.disableFundsOriginFields();
        }
      });

    this.formSubmitSubject$
      .pipe(
        tap(() => {
          this.beneficiaryDataForm.markAsDirty();
          this.beneficiaryDataForm.markAllAsTouched();
          Object.keys(this.beneficiaryDataForm.controls).forEach(key => {
            const control = this.beneficiaryDataForm.get(key);
            control?.updateValueAndValidity({ emitEvent: true });
          });

          this.shareHolders.controls.forEach(control => {
            control.markAsDirty();
            control.markAllAsTouched();
            control.updateValueAndValidity();
          });
        }),
        filter(() => this.beneficiaryDataForm.valid && this.isTaxReportValid)
      )
      .subscribe(() => {
        const rawValue = this.beneficiaryDataForm.getRawValue();
        let cleanedData = this.removeBeneficiaryRepresentativeFields(rawValue);
        cleanedData = this.removeEmptyFields(cleanedData);
        this.nextStep.next(cleanedData);
      });

    this.updateFieldValidation();

    if (this.initData) {
      this.populateFormData();
    }

    this.initializeOtherOptionValidators();

    // Disable isBeneficiary and funds origin fields by default until tax report is validated
    this.beneficiaryDataForm.get('isBeneficiary').disable();
    this.disableFundsOriginFields();
  }

  onContinue(): void {
    this.formSubmitSubject$.next();
  }

  onTaxReportFileLoaded(event: any): void {
    this.spinner.show();
    this.taxReportErrorMessage = '';
    this.isTaxReportValid = false;
    // Disable isBeneficiary and funds origin fields while verifying
    this.beneficiaryDataForm.get('isBeneficiary').disable();
    this.disableFundsOriginFields();

    const taxReportPayload =
      this.role === Roles.CLIENT
        ? this.laftService.validateTaxReport(event)
        : this.laftService.verifyTaxReport({ taxReport: event });

    taxReportPayload.subscribe(
      (response: any) => {
        console.log('response', response);
        console.log('taxReportPayload', taxReportPayload);
        this.spinner.hide();

        if (response?.validateUrl?.status === 'valid') {
          this.taxReportErrorMessage = '';
          this.isTaxReportValid = true;
          this.beneficiaryDataForm.get('isBeneficiary').enable();
          // Enable fields first, then set validators to prevent premature validation triggers
          this.enableFundsOriginFields();
          // Set validators after enabling to avoid triggering statusChanges during enable
          this.setFundsOriginValidators();
          this.initializeOtherOptionValidators();
          this.shareHolders.clear();
          if (response?.data?.shareholders && Array.isArray(response.data.shareholders)) {
            const shareholdersWithMinShareholding = response.data.shareholders.filter(
              (shareholder: any) => shareholder.shareholding >= 10
            );
            shareholdersWithMinShareholding.forEach((shareholder: any) => {
              this.addLegalRepresentative(shareholder);
            });
          }
          if (this.shareHolders.length === 0) {
            this.addLegalRepresentative();
          }
        } else if (response?.validateUrl?.status === 'invalid' || response?.validateUrl?.status === 'error') {
          this.revertToDefaultValues();
          this.beneficiaryDataForm.get('isBeneficiary').disable();
          this.disableFundsOriginFields();
          this.taxReportErrorMessage = 'Reporte tributario no vigente';
          this.isTaxReportValid = false;
          this.shareHolders.clear();
        }
      },
      (error: any) => {
        console.error('Error verifying tax report:', error);
        this.spinner.hide();
        this.revertToDefaultValues();
        this.beneficiaryDataForm.get('isBeneficiary').disable();
        this.disableFundsOriginFields();
        this.taxReportErrorMessage = 'Reporte tributario no vigente';
        this.isTaxReportValid = false;
        this.shareHolders.clear();
      }
    );
  }

  private disableFundsOriginFields(): void {
    // Use emitEvent: false to prevent triggering statusChanges when disabling
    this.beneficiaryDataForm.get('fundsOrigin')?.disable({ emitEvent: false });
    this.beneficiaryDataForm.get('fundsOriginOtherOption')?.disable({ emitEvent: false });
    this.beneficiaryDataForm.get('fundsOriginLocation')?.disable({ emitEvent: false });
    this.beneficiaryDataForm.get('fundsOriginLocationOtherOption')?.disable({ emitEvent: false });
  }

  private enableFundsOriginFields(): void {
    // Enable fields without triggering validation by using emitEvent: false
    // This prevents the select component's statusChanges subscription from firing prematurely
    this.beneficiaryDataForm.get('fundsOrigin')?.enable({ emitEvent: false });
    this.beneficiaryDataForm.get('fundsOriginOtherOption')?.enable({ emitEvent: false });
    this.beneficiaryDataForm.get('fundsOriginLocation')?.enable({ emitEvent: false });
    this.beneficiaryDataForm.get('fundsOriginLocationOtherOption')?.enable({ emitEvent: false });
  }

  private setFundsOriginValidators(): void {
    // Only set validators when tax report is valid
    // Funds origin fields are always required when tax report is valid (independent of isBeneficiary)
    // Use emitEvent: false to prevent triggering statusChanges subscriptions in select components
    // This prevents showing validation errors before the user interacts with the fields
    this.beneficiaryDataForm.get('fundsOrigin')?.setValidators([Validators.required]);
    this.beneficiaryDataForm.get('fundsOriginLocation')?.setValidators([Validators.required]);
  }

  private revertToDefaultValues(): void {
    const fundsOriginControl = this.beneficiaryDataForm.get('fundsOrigin');
    const fundsOriginLocationControl = this.beneficiaryDataForm.get('fundsOriginLocation');

    // Reset isBeneficiary and all representative-related fields to default
    this.beneficiaryDataForm.patchValue(
      {
        isBeneficiary: true,
        beneficiaryRepresentativeType: null,
        beneficiaryBusinessName: '',
        beneficiaryRuc: '',
        beneficiaryPowerType: 'acta',
        beneficiaryDocument: null,
        beneficiaryPersonName: '',
        beneficiaryPersonDocumentNumber: '',
        beneficiaryPersonDocumentType: 'national id',
        beneficiaryPersonPowerType: 'escritura_publica',
        beneficiaryPersonDocument: null,
        isBeneficiaryPersonPep: false,
        beneficiaryPersonCharge: '',
        beneficiaryPersonInstitution: '',
        fundsOrigin: '',
        fundsOriginOtherOption: '',
        fundsOriginLocation: '',
        fundsOriginLocationOtherOption: '',
      },
      { emitEvent: false }
    );

    // Clear legal representatives array
    this.shareHolders.clear();

    // Clear validators for dependent fields
    this.beneficiaryDataForm.get('beneficiaryRepresentativeType').clearValidators();
    this.beneficiaryDataForm.get('beneficiaryBusinessName').clearValidators();
    this.beneficiaryDataForm.get('beneficiaryRuc').clearValidators();
    this.beneficiaryDataForm.get('beneficiaryPowerType').clearValidators();
    this.beneficiaryDataForm.get('beneficiaryDocument').clearValidators();
    this.beneficiaryDataForm.get('beneficiaryPersonName').clearValidators();
    this.beneficiaryDataForm.get('beneficiaryPersonDocumentNumber').clearValidators();
    this.beneficiaryDataForm.get('beneficiaryPersonDocumentType').clearValidators();
    this.beneficiaryDataForm.get('beneficiaryPersonPowerType').clearValidators();
    this.beneficiaryDataForm.get('beneficiaryPersonDocument').clearValidators();
    this.beneficiaryDataForm.get('isBeneficiaryPersonPep').clearValidators();
    this.beneficiaryDataForm.get('beneficiaryPersonCharge').clearValidators();
    this.beneficiaryDataForm.get('beneficiaryPersonInstitution').clearValidators();
    this.beneficiaryDataForm.get('fundsOrigin').clearValidators();
    this.beneficiaryDataForm.get('fundsOriginOtherOption').clearValidators();
    this.beneficiaryDataForm.get('fundsOriginLocation').clearValidators();
    this.beneficiaryDataForm.get('fundsOriginLocationOtherOption').clearValidators();

    // Reset touched and dirty state FIRST, before updating validity
    // This prevents statusChanges from firing with touched=true state
    fundsOriginControl?.markAsUntouched();
    fundsOriginControl?.markAsPristine();
    fundsOriginLocationControl?.markAsUntouched();
    fundsOriginLocationControl?.markAsPristine();

    // Update validity for all affected controls
    this.beneficiaryDataForm.get('beneficiaryRepresentativeType').updateValueAndValidity();
    this.beneficiaryDataForm.get('beneficiaryBusinessName').updateValueAndValidity();
    this.beneficiaryDataForm.get('beneficiaryRuc').updateValueAndValidity();
    this.beneficiaryDataForm.get('beneficiaryPowerType').updateValueAndValidity();
    this.beneficiaryDataForm.get('beneficiaryDocument').updateValueAndValidity();
    this.beneficiaryDataForm.get('beneficiaryPersonName').updateValueAndValidity();
    this.beneficiaryDataForm.get('beneficiaryPersonDocumentNumber').updateValueAndValidity();
    this.beneficiaryDataForm.get('beneficiaryPersonDocumentType').updateValueAndValidity();
    this.beneficiaryDataForm.get('beneficiaryPersonPowerType').updateValueAndValidity();
    this.beneficiaryDataForm.get('beneficiaryPersonDocument').updateValueAndValidity();
    this.beneficiaryDataForm.get('isBeneficiaryPersonPep').updateValueAndValidity();
    this.beneficiaryDataForm.get('beneficiaryPersonCharge').updateValueAndValidity();
    this.beneficiaryDataForm.get('beneficiaryPersonInstitution').updateValueAndValidity();
    // Update funds origin fields validity with emitEvent: false since validators are cleared
    this.beneficiaryDataForm.get('fundsOrigin').updateValueAndValidity({ emitEvent: false });
    this.beneficiaryDataForm.get('fundsOriginOtherOption').updateValueAndValidity({ emitEvent: false });
    this.beneficiaryDataForm.get('fundsOriginLocation').updateValueAndValidity({ emitEvent: false });
    this.beneficiaryDataForm.get('fundsOriginLocationOtherOption').updateValueAndValidity({ emitEvent: false });
  }

  private removeEmptyFields(data: any): any {
    const cleaned = { ...data };

    if (cleaned.fundsOrigin !== 'other') {
      delete cleaned.fundsOriginOtherOption;
    } else if (!cleaned.fundsOriginOtherOption || cleaned.fundsOriginOtherOption.trim() === '') {
      delete cleaned.fundsOriginOtherOption;
    }

    if (cleaned.fundsOriginLocation !== 'other') {
      delete cleaned.fundsOriginLocationOtherOption;
    } else if (!cleaned.fundsOriginLocationOtherOption || cleaned.fundsOriginLocationOtherOption.trim() === '') {
      delete cleaned.fundsOriginLocationOtherOption;
    }

    Object.keys(cleaned).forEach(key => {
      if (
        cleaned[key] === null ||
        cleaned[key] === '' ||
        cleaned[key] === undefined ||
        cleaned[key] === 'Fecha invalida'
      ) {
        delete cleaned[key];
      }
    });

    if (this.isTaxReportValid && cleaned?.shareHolders && Array.isArray(cleaned?.shareHolders)) {
      cleaned.shareHolders = cleaned.shareHolders.filter(
        (representative: any) =>
          representative.fullName?.trim() &&
          representative.documentType?.trim() &&
          representative.documentNumber?.trim()
      );
      if (cleaned.shareHolders.length === 0) {
        delete cleaned.shareHolders;
      }
    } else {
      delete cleaned.shareHolders;
    }

    return cleaned;
  }

  private removeBeneficiaryRepresentativeFields(data: any): any {
    const cleaned = { ...data };

    if (!cleaned.isBeneficiary && cleaned.beneficiaryRepresentativeType === 'persona_juridica') {
      cleaned.beneficiaryPersonName = '';
      cleaned.beneficiaryPersonDocumentNumber = '';
      cleaned.beneficiaryPersonDocumentType = '';
      cleaned.beneficiaryPersonPowerType = '';
      cleaned.beneficiaryPersonDocument = null;
      cleaned.isBeneficiaryPersonPep = false;
      cleaned.beneficiaryPersonCharge = '';
      cleaned.beneficiaryPersonInstitution = '';
      return this.removeEmptyOptionalFields(cleaned);
    }

    if (!cleaned.isBeneficiary && cleaned.beneficiaryRepresentativeType === 'persona_natural') {
      cleaned.beneficiaryBusinessName = '';
      cleaned.beneficiaryRuc = '';
      cleaned.beneficiaryPowerType = '';
      cleaned.beneficiaryDocument = null;

      if (!cleaned.isBeneficiaryPersonPep) {
        cleaned.beneficiaryPersonCharge = '';
        cleaned.beneficiaryPersonInstitution = '';
      }

      return this.removeEmptyOptionalFields(cleaned);
    }

    cleaned.beneficiaryRepresentativeType = '';
    cleaned.beneficiaryBusinessName = '';
    cleaned.beneficiaryRuc = '';
    cleaned.beneficiaryPowerType = '';
    cleaned.beneficiaryDocument = null;
    cleaned.beneficiaryPersonName = '';
    cleaned.beneficiaryPersonDocumentNumber = '';
    cleaned.beneficiaryPersonDocumentType = '';
    cleaned.beneficiaryPersonPowerType = '';
    cleaned.beneficiaryPersonDocument = null;
    cleaned.isBeneficiaryPersonPep = false;
    cleaned.beneficiaryPersonCharge = '';
    cleaned.beneficiaryPersonInstitution = '';
    return this.removeEmptyOptionalFields(cleaned);
  }

  updateFieldValidation() {
    this.beneficiaryDataForm
      .get('isBeneficiary')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        if (!value) {
          this.beneficiaryDataForm.get('beneficiaryRepresentativeType').setValidators([Validators.required]);

          // Set default values only when no representative type is selected yet (do not overwrite persona_juridica from initData or user choice)
          const currentRepType = this.beneficiaryDataForm.get('beneficiaryRepresentativeType')?.value;
          const needsDefaultType = currentRepType === null || currentRepType === undefined || currentRepType === '';
          if (needsDefaultType) {
            this.beneficiaryDataForm.patchValue(
              {
                beneficiaryRepresentativeType: 'persona_natural',
                beneficiaryPersonDocumentType: 'national id',
                beneficiaryPersonPowerType: 'escritura_publica',
              },
              { emitEvent: false }
            );
          }
        } else {
          this.beneficiaryDataForm.get('beneficiaryRepresentativeType').clearValidators();
          this.beneficiaryDataForm.get('beneficiaryBusinessName').clearValidators();
          this.beneficiaryDataForm.get('beneficiaryRuc').clearValidators();
          this.beneficiaryDataForm.get('beneficiaryPowerType').clearValidators();
          this.beneficiaryDataForm.get('beneficiaryDocument').clearValidators();
          this.beneficiaryDataForm.get('beneficiaryPersonName').clearValidators();
          this.beneficiaryDataForm.get('beneficiaryPersonDocumentNumber').clearValidators();
          this.beneficiaryDataForm.get('beneficiaryPersonDocumentType').clearValidators();
          this.beneficiaryDataForm.get('beneficiaryPersonPowerType').clearValidators();
          this.beneficiaryDataForm.get('beneficiaryPersonDocument').clearValidators();
          this.beneficiaryDataForm.get('isBeneficiaryPersonPep').clearValidators();
          this.beneficiaryDataForm.get('beneficiaryPersonCharge').clearValidators();
          this.beneficiaryDataForm.get('beneficiaryPersonInstitution').clearValidators();

          this.beneficiaryDataForm.patchValue({
            beneficiaryRepresentativeType: '',
            beneficiaryPersonDocumentType: '',
            beneficiaryPersonPowerType: '',
            beneficiaryBusinessName: '',
            beneficiaryRuc: '',
            beneficiaryPowerType: 'acta',
            beneficiaryDocument: null,
            beneficiaryPersonName: '',
            beneficiaryPersonDocumentNumber: '',
            beneficiaryPersonDocument: null,
            isBeneficiaryPersonPep: false,
            beneficiaryPersonCharge: '',
            beneficiaryPersonInstitution: '',
          });

          this.beneficiaryDataForm.get('beneficiaryBusinessName').updateValueAndValidity();
          this.beneficiaryDataForm.get('beneficiaryRuc').updateValueAndValidity();
          this.beneficiaryDataForm.get('beneficiaryPowerType').updateValueAndValidity();
          this.beneficiaryDataForm.get('beneficiaryDocument').updateValueAndValidity();
          this.beneficiaryDataForm.get('beneficiaryPersonName').updateValueAndValidity();
          this.beneficiaryDataForm.get('beneficiaryPersonDocumentNumber').updateValueAndValidity();
          this.beneficiaryDataForm.get('beneficiaryPersonDocumentType').updateValueAndValidity();
          this.beneficiaryDataForm.get('beneficiaryPersonPowerType').updateValueAndValidity();
          this.beneficiaryDataForm.get('beneficiaryPersonDocument').updateValueAndValidity();
          this.beneficiaryDataForm.get('isBeneficiaryPersonPep').updateValueAndValidity();
          this.beneficiaryDataForm.get('beneficiaryPersonCharge').updateValueAndValidity();
          this.beneficiaryDataForm.get('beneficiaryPersonInstitution').updateValueAndValidity();
        }

        this.beneficiaryDataForm.get('beneficiaryRepresentativeType').updateValueAndValidity();
        this.updatePepFieldsValidation();
      });

    this.beneficiaryDataForm
      .get('beneficiaryRepresentativeType')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        const businessNameControl = this.beneficiaryDataForm.get('beneficiaryBusinessName');
        const rucControl = this.beneficiaryDataForm.get('beneficiaryRuc');
        const powerTypeControl = this.beneficiaryDataForm.get('beneficiaryPowerType');
        const documentControl = this.beneficiaryDataForm.get('beneficiaryDocument');

        const personNameControl = this.beneficiaryDataForm.get('beneficiaryPersonName');
        const personDocumentTypeControl = this.beneficiaryDataForm.get('beneficiaryPersonDocumentType');
        const personDocumentNumberControl = this.beneficiaryDataForm.get('beneficiaryPersonDocumentNumber');
        const personPowerTypeControl = this.beneficiaryDataForm.get('beneficiaryPersonPowerType');
        const personDocumentControl = this.beneficiaryDataForm.get('beneficiaryPersonDocument');

        if (value === 'persona_juridica' && !this.beneficiaryDataForm.get('isBeneficiary').value) {
          businessNameControl.setValidators([Validators.required]);
          rucControl.setValidators([Validators.required, RucExactLengthValidator(11)]);
          powerTypeControl.setValidators([Validators.required]);
          documentControl.setValidators([Validators.required]);

          personNameControl.clearValidators();
          personDocumentNumberControl.clearValidators();
          personDocumentTypeControl.clearValidators();
          personPowerTypeControl.clearValidators();
          personDocumentControl.clearValidators();

          personNameControl.setValue('');
          personDocumentNumberControl.setValue('');
          personDocumentTypeControl.setValue('national id');
          personPowerTypeControl.setValue('escritura_publica');
          personDocumentControl.setValue(null);
        } else if (value === 'persona_natural' && !this.beneficiaryDataForm.get('isBeneficiary').value) {
          personNameControl.setValidators([RequiredValidator, LettersOnlyValidator]);
          personDocumentTypeControl.setValidators([Validators.required]);
          personDocumentNumberControl.setValidators([
            RequiredValidator,
            IDFormatBeneficiaryValidator,
            IDLengthBeneficiaryValidator,
          ]);
          personPowerTypeControl.setValidators([Validators.required]);
          personDocumentControl.setValidators([Validators.required]);

          businessNameControl.clearValidators();
          rucControl.clearValidators();
          powerTypeControl.clearValidators();
          documentControl.clearValidators();

          businessNameControl.setValue('');
          rucControl.setValue('');
          powerTypeControl.setValue('acta');
          documentControl.setValue(null);
        } else {
          businessNameControl.clearValidators();
          rucControl.clearValidators();
          powerTypeControl.clearValidators();
          documentControl.clearValidators();

          personNameControl.clearValidators();
          personDocumentNumberControl.clearValidators();
          personDocumentTypeControl.clearValidators();
          personPowerTypeControl.clearValidators();
          personDocumentControl.clearValidators();
        }

        businessNameControl.updateValueAndValidity();
        rucControl.updateValueAndValidity();
        powerTypeControl.updateValueAndValidity();
        documentControl.updateValueAndValidity();

        personNameControl.updateValueAndValidity();
        personDocumentTypeControl.updateValueAndValidity();
        personDocumentNumberControl.updateValueAndValidity();
        personPowerTypeControl.updateValueAndValidity();
        personDocumentControl.updateValueAndValidity();

        this.updatePepFieldsValidation();
      });

    this.beneficiaryDataForm
      .get('fundsOrigin')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        const fundsOriginOtherControl = this.beneficiaryDataForm.get('fundsOriginOtherOption');
        if (value === 'other') {
          fundsOriginOtherControl.setValidators([Validators.required]);
        } else {
          fundsOriginOtherControl.clearValidators();
          fundsOriginOtherControl.setValue('');
        }

        fundsOriginOtherControl.updateValueAndValidity();
      });

    this.beneficiaryDataForm
      .get('fundsOriginLocation')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        const fundsOriginLocationOtherControl = this.beneficiaryDataForm.get('fundsOriginLocationOtherOption');

        if (value === 'other') {
          fundsOriginLocationOtherControl.setValidators([Validators.required]);
        } else {
          fundsOriginLocationOtherControl.clearValidators();
          fundsOriginLocationOtherControl.setValue('');
        }

        fundsOriginLocationOtherControl.updateValueAndValidity();
      });

    this.beneficiaryDataForm
      .get('isBeneficiaryPersonPep')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updatePepFieldsValidation();
      });
  }

  private updatePepFieldsValidation(): void {
    const isBeneficiary = this.beneficiaryDataForm.get('isBeneficiary').value;
    const representativeType = this.beneficiaryDataForm.get('beneficiaryRepresentativeType').value;
    const isPep = this.beneficiaryDataForm.get('isBeneficiaryPersonPep').value;
    const chargeControl = this.beneficiaryDataForm.get('beneficiaryPersonCharge');
    const institutionControl = this.beneficiaryDataForm.get('beneficiaryPersonInstitution');

    if (!isBeneficiary && representativeType === 'persona_natural' && isPep) {
      chargeControl.setValidators([Validators.required]);
      institutionControl.setValidators([Validators.required]);
    } else {
      chargeControl.clearValidators();
      institutionControl.clearValidators();
      if (!isPep) {
        chargeControl.setValue('');
        institutionControl.setValue('');
      }
    }
    chargeControl.updateValueAndValidity();
    institutionControl.updateValueAndValidity();
  }

  get isPersonalNatural() {
    return this.beneficiaryDataForm.get('beneficiaryRepresentativeType').value === 'persona_natural';
  }

  get isBeneficiaryPersonPep() {
    return this.beneficiaryDataForm.get('isBeneficiaryPersonPep').value;
  }

  get shareHolders(): FormArray {
    return this.beneficiaryDataForm.get('shareHolders') as FormArray;
  }

  get taxReportDataUrl(): string | null {
    if (this.role !== Roles.CLIENT) return null;
    return this.initData?.taxReport
      ? `data:application/pdf;base64,${this.initData.taxReport}`
      : null;
  }

  get taxReportFileName(): string {
    return this.initData?.taxReportFileName || 'Reporte Tributario';
  }

  addLegalRepresentative(shareholder?: any): void {
    const isFirstMember = this.shareHolders.length === 0;
    const formGroup = this.createLegalRepresentativeFormGroup(isFirstMember);

    if (shareholder) {
      const documentTypeMap: { [key: string]: string } = {
        DNI: 'national id',
        'Carnet Extranjería': 'foreigner card',
        Pasaporte: 'passport',
        RUC: 'ruc',
      };

      const mappedDocumentType = !shareholder.documentType
        ? 'other'
        : documentTypeMap[shareholder.documentType] ||
          (this.legalRepresentativeIdTypeOptions.some(opt => opt.value === shareholder.documentType)
            ? shareholder.documentType
            : 'other');

      formGroup.patchValue({
        fullName: shareholder.name || '',
        documentType: mappedDocumentType,
        documentNumber: shareholder.documentNumber || '',
      });
    }

    this.shareHolders.push(formGroup);
  }

  createLegalRepresentativeFormGroup(isRequired: boolean = false): FormGroup {
    const formGroup = this.formBuilder.group({
      fullName: ['', isRequired ? [Validators.required] : []],
      documentType: ['national id', isRequired ? [Validators.required] : []],
      documentNumber: ['', isRequired ? [Validators.required] : []],
    });

    const documentNumberControl = formGroup.get('documentNumber');
    if (documentNumberControl) {
      const validators: any[] = [];
      if (isRequired) {
        validators.push(Validators.required);
      }
      validators.push(IDFormatLegalRepresentativeValidator);
      validators.push(IDLengthLegalRepresentativeValidator);
      documentNumberControl.setValidators(validators);
    }

    formGroup
      .get('documentType')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        documentNumberControl?.updateValueAndValidity();
      });

    return formGroup;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeOtherOptionValidators(): void {
    const fundsOrigin = this.beneficiaryDataForm.get('fundsOrigin')?.value;
    const fundsOriginLocation = this.beneficiaryDataForm.get('fundsOriginLocation')?.value;
    const fundsOriginOtherControl = this.beneficiaryDataForm.get('fundsOriginOtherOption');
    const fundsOriginLocationOtherControl = this.beneficiaryDataForm.get('fundsOriginLocationOtherOption');

    if (fundsOrigin === 'other') {
      fundsOriginOtherControl?.setValidators([Validators.required]);
    } else {
      fundsOriginOtherControl?.clearValidators();
    }

    if (fundsOriginLocation === 'other') {
      fundsOriginLocationOtherControl?.setValidators([Validators.required]);
    } else {
      fundsOriginLocationOtherControl?.clearValidators();
    }

    fundsOriginOtherControl?.updateValueAndValidity({ emitEvent: false });
    fundsOriginLocationOtherControl?.updateValueAndValidity({ emitEvent: false });
  }

  private removeEmptyOptionalFields(cleaned: any): any {
    const optionalFields = [
      'beneficiaryRepresentativeType',
      'beneficiaryBusinessName',
      'beneficiaryRuc',
      'beneficiaryPowerType',
      'beneficiaryDocument',
      'beneficiaryPersonName',
      'beneficiaryPersonDocumentNumber',
      'beneficiaryPersonDocumentType',
      'beneficiaryPersonPowerType',
      'beneficiaryPersonDocument',
      'beneficiaryPersonCharge',
      'beneficiaryPersonInstitution',
    ];

    if (cleaned.isBeneficiary === true) {
      optionalFields.forEach(field => {
        if (field in cleaned) {
          delete cleaned[field];
        }
      });
      return cleaned;
    }

    optionalFields.forEach(field => {
      if (!(field in cleaned)) {
        return;
      }

      const value = cleaned[field];
      if (typeof value === 'string') {
        cleaned[field] = value.trim();
      }

      if (cleaned[field] === '' || cleaned[field] === null || cleaned[field] === undefined) {
        delete cleaned[field];
      }
    });

    return cleaned;
  }

  private populateFormData(): void {
    const isBeneficiary = this.initData?.isBeneficiary ?? true;

    this.beneficiaryDataForm.patchValue(
      {
        isBeneficiary: isBeneficiary,
        beneficiaryRepresentativeType:
          this.initData?.beneficiaryRepresentativeType || (!isBeneficiary ? 'persona_natural' : null),
        beneficiaryBusinessName: this.initData?.beneficiaryBusinessName || '',
        beneficiaryRuc: this.initData?.beneficiaryRuc || '',
        beneficiaryPowerType: this.initData?.beneficiaryPowerType || 'acta',
        beneficiaryPersonName: this.initData?.beneficiaryPersonName || '',
        beneficiaryPersonDocumentNumber: this.initData?.beneficiaryPersonDocumentNumber || '',
        beneficiaryPersonDocumentType:
          this.initData?.beneficiaryPersonDocumentType || (!isBeneficiary ? 'national id' : ''),
        beneficiaryPersonPowerType:
          this.initData?.beneficiaryPersonPowerType || (!isBeneficiary ? 'escritura_publica' : ''),
        fundsOrigin: this.initData?.fundsOrigin || '',
        fundsOriginOtherOption: this.initData?.fundsOriginOtherOption || '',
        fundsOriginLocation: this.initData?.fundsOriginLocation || '',
        fundsOriginLocationOtherOption: this.initData?.fundsOriginLocationOtherOption || '',
        isBeneficiaryPersonPep: this.initData?.isBeneficiaryPersonPep || false,
        beneficiaryPersonCharge: this.initData?.beneficiaryPersonCharge || '',
        beneficiaryPersonInstitution: this.initData?.beneficiaryPersonInstitution || '',
      },
      { emitEvent: false }
    );

    // If google drive returned a tax report, trigger the same flow as manual upload:
    if (this.role === Roles.CLIENT && this.initData?.taxReport) {
      this.onTaxReportFileLoaded(this.taxReportDataUrl);
    }
  }
}
